<template>
  <Modal :is-open="isOpen" :default-success-msg="$t('Success')">
    <template #form="{}">
      <transition
        mode="out-in"
        enter-active-class="transition duration-300 ease-out transform"
        enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0">
        <div v-if="!add_new_field_is_shown">
          <h3 class="text-2xl font-medium leading-10 text-center text-gray-900 capitalize dark:text-white">
            <span class="capitalize">{{ $t("Update") }} {{ selectedRecord?.type == "company" ? "Company" : selectedRecord?.type }} </span>
          </h3>
          <Form
            v-slot="{ isSubmitting }"
            :validation-schema="schema"
            class="space-y-6 overflow-visible min-h-[250px] flex flex-col"
            @submit="updateRecord()">
            <div class="mb-auto -space-y-px rounded-md shadow-sm">
              <div v-if="record_metrics.length > 0" class="p-3 mt-5 space-y-6 rounded-sm">
                <div class="grid w-full h-full grid-cols-1 gap-2 sm:grid-cols-2">
                  <div v-for="metric in record_metrics" :key="metric.id" class="mb-2">
                    <MetricFieldInput v-model:metric-value="metric.value" :metric="metric" :show-success-check-mark="true" class="w-full" />
                  </div>
                </div>
              </div>

              <div class="flex justify-end px-3">
                <div v-if="selectedRecord?.type == 'contact'" class="primary-outline-btn w-fit" @click="handleSendEmail">
                  <Mail class="w-5 h-5 mr-1" />
                  <div class="pl-1">
                    {{ $t("Send Email") }}
                  </div>
                </div>

                <div class="primary-outline-btn w-fit" @click="add_new_field_is_shown = true">
                  <Plus class="w-5 h-5" />
                  <div class="pl-1">
                    {{ $t("Add/Remove Metrics") }}
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-row justify-between w-full gap-2 pt-2">
              <button type="button" class="w-1/2 m-0 cancel-btn dark:border-dark-divider-border" @click="$emit('update:isOpen', false)">
                {{ $t("Cancel") }}
              </button>
              <button type="submit" :disabled="isSubmitting" class="w-1/2 primary-btn">
                <span>{{ $t("Update") }}</span>
              </button>
            </div>
          </Form>
        </div>
        <div v-else>
          <h3 class="text-2xl font-medium leading-10 text-center text-gray-800 dark:text-white">
            {{ $t("New Field") }}
          </h3>
          <NewFieldForm
            v-model:included-metrics="record_metrics"
            :component-reference="componentReference"
            :page-reference="pageReference"
            :object-type="selectedRecord.type"
            :view-type="VIEW_TYPES.FORM_UPDATE"
            @cancel="
              () => {
                prepareValidationSchema()
                add_new_field_is_shown = false
              }
            "
            @done="
              () => {
                prepareValidationSchema()
                add_new_field_is_shown = false
              }
            " />
        </div>
      </transition>
    </template>
  </Modal>
</template>
<script>
import * as Yup from "yup"
import Modal from "@/components/shared/Modal.vue"
import { Form } from "vee-validate"
import NewFieldForm from "@/components/forms/NewFieldForm.vue"
import MetricFieldInput from "@/components/shared/MetricFieldInput.vue"
import { VIEW_TYPES } from "@/store/constants/references"
import { Plus } from "lucide-vue-next"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { Mail } from "lucide-vue-next"

export default {
  components: {
    Plus,
    MetricFieldInput,
    NewFieldForm,
    Form,
    Modal,
    Mail
  },
  inject: ["getViewMetrics"],
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    selectedRecord: {
      type: Object,
      default: () => {}
    },
    pageReference: {
      type: String,
      required: true
    },
    componentReference: {
      type: String,
      required: false
    }
  },
  emits: ["updateMap", "update:isOpen"],
  data() {
    return {
      record_metrics: [],
      add_new_field_is_shown: false,
      VIEW_TYPES,
      schema: {},
      view_metrics: []
    }
  },
  computed: {},
  watch: {
    isOpen() {
      if (this.isOpen) {
        this.record_metrics = this.view_metrics?.map((metric) => {
          return {
            ...metric,
            value: this.selectedRecord?.values ? this.selectedRecord?.values[metric.reference_name] : null,
            is_checked: true
          }
        })
        this.setObjectMetrics()
      } else {
        setTimeout(() => {
          this.record_metrics = []
          this.add_new_field_is_shown = false
        }, 500)
      }
    },
    view_metrics() {
      if (this.view_metrics) {
        this.record_metrics = this.view_metrics?.map((metric) => {
          return { ...metric, value: this.getMetricValue(metric.reference_name), is_checked: true }
        })
        this.setObjectMetrics()
      }
    }
  },
  created() {
    this.initViewMetrics()
    this.register_listeners()
  },
  activated() {
    this.register_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },

  methods: {
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.PORTFOLIO_UPDATED_IN_UPDATE_RECORD_FORM, () => {
        this.initViewMetrics()
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.PORTFOLIO_UPDATED_IN_UPDATE_RECORD_FORM)
    },
    initViewMetrics() {
      const metrics_obj = {
        page_reference: this.pageReference,
        object_type: this.selectedRecord?.type,
        view_type: VIEW_TYPES.FORM_UPDATE,
        flat: false
      }
      if (this.$route.params?.page) {
        metrics_obj["layout_page_reference"] = this.$route.params?.page
      }
      if (this.componentReference) {
        metrics_obj["component_reference"] = this.componentReference
      }

      this.view_metrics = this.getViewMetrics(metrics_obj)
      this.setObjectMetrics()
    },
    handleSendEmail() {
      if (this.$store.state.data.records_fetched[this.selectedRecord.id]) {
        const i_service = this.$store.state.integration.integrations.find((i) => i.name == "gmail")
        if (i_service && i_service.scope.includes("https://www.googleapis.com/auth/gmail.modify")) {
          if (i_service.status == "on") {
            this.$emitter.emit(ALL_EMITTERS.SEND_EMAIL, {
              records: [],
              record: this.$store.state.data.records_fetched[this.selectedRecord.id],
              object_type: this.$store.state.data.records_fetched[this.selectedRecord.id].type
            })
          } else {
            this.$emitter.emit(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, {
              message: this.$t("Email integration is not active, Activate integration to send an email")
            })
          }
        } else {
          this.$emitter.emit(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, {
            message: this.$t("In order to send an email please link your account to your Email.")
          })
        }
      } else {
        this.$store.dispatch("data/getRecord", this.selectedRecord.id).then((res) => {
          const i_service = this.$store.state.integration.integrations.find((i) => i.name == "gmail")
          if (i_service && i_service.scope.includes("https://www.googleapis.com/auth/gmail.modify")) {
            if (i_service.status == "on") {
              this.$emitter.emit(ALL_EMITTERS.SEND_EMAIL, { records: [], record: res.data, object_type: res.data.type })
            } else {
              this.$emitter.emit(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, {
                message: this.$t("Email integration is not active, Activate integration to send an email")
              })
            }
          } else {
            this.$emitter.emit(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, {
              message: this.$t("In order to send an email please link your account to your Email.")
            })
          }
        })
      }
    },
    setObjectMetrics() {
      this.record_metrics = this.view_metrics?.map((metric) => {
        return {
          ...metric,
          value: this.selectedRecord?.values ? this.selectedRecord?.values[metric.reference_name] : null,
          is_checked: true
        }
      })
      this.prepareValidationSchema()
    },
    getMetricValue(reference_name) {
      return this.$utils.find(this.record_metrics, { reference_name: reference_name })?.value
    },
    prepareValidationSchema() {
      this.schema = {}
      let required_metrics = [
        this.$utils.getOptionMetricReference("company", "website"),
        this.$utils.getOptionMetricReference("company", "name"),
        this.$utils.getOptionMetricReference("contact", "first_name"),
        this.$utils.getOptionMetricReference("contact", "last_name"),
        this.$utils.getOptionMetricReference("contact", "email"),
        this.$utils.getOptionMetricReference("department", "name"),
        this.$utils.getOptionMetricReference("opportunity", "name"),
        this.$utils.getOptionMetricReference("opportunity", "amount")
      ]

      this.record_metrics?.forEach((metric) => {
        if (required_metrics.includes(metric.reference_name)) {
          if (metric.input_type == "URL") {
            this.schema[metric.reference_name] = Yup.string()
              .matches(
                /^(http[s]?:\/\/(www\.)?|ftp:\/\/(www\.)?|www\.){0,1}([0-9A-Za-z-\\.@:%_\\+~#=]+)+((\.[a-zA-Z]{2,3})+)(\/(.)*)?(\?(.)*)?/,
                this.$t("Please enter a valid url address")
              )
              .required(`${metric.name} ` + this.$t("is required"))
          } else if (metric.data_type == "Number") {
            this.schema[metric.reference_name] = Yup.number(this.$t("Please enter a valid number")).required(
              `${metric.name} ` + this.$t("is required")
            )
          } else if (metric.input_type == "Email") {
            this.schema[metric.reference_name] = Yup.string().email(this.$t("Please enter a valid email address"))
          } else {
            this.schema[metric.reference_name] = Yup.string().required(`${metric.name} ` + this.$t("is required"))
          }
        } else {
          if (metric.input_type == "URL") {
            this.schema[metric.reference_name] = Yup.string()
              .matches(
                /^(http[s]?:\/\/(www\.)?|ftp:\/\/(www\.)?|www\.){0,1}([0-9A-Za-z-\\.@:%_\\+~#=]+)+((\.[a-zA-Z]{2,3})+)(\/(.)*)?(\?(.)*)?/,
                this.$t("Please enter a valid url address")
              )
              .nullable()
          } else if (metric.data_type == "Number") {
            this.schema[metric.reference_name] = Yup.string()
              .matches(/^[0-9-.]+$/, this.$t("Please enter a valid number"))
              .nullable()
          } else if (metric.input_type == "Email") {
            this.schema[metric.reference_name] = Yup.string().email(this.$t("Please enter a valid email address")).nullable()
          } else {
            this.schema[metric.reference_name] = Yup.string().nullable()
          }
        }
      })
    },

    updateRecord() {
      let metrics_values = {}
      this.record_metrics?.forEach((metric) => {
        metrics_values[metric.reference_name] = metric.value
      })

      this.$store.dispatch("data/updateRecord", { record_id: this.selectedRecord.id, data: { values: metrics_values } }).then(() => {
        this.$emit("updateMap")
        this.$emit("update:isOpen", false)
        this.$toast.open({
          message: `${this.selectedRecord?.type} ` + this.$t("updated successfully"),
          position: "top",
          type: "success",
          duration: 3000
        })
      })
    }
  }
}
</script>

<style scoped>
.org-tree-container {
  background-color: transparent !important;
}
.org-tree-node-label-inner {
  padding: 0px !important;
}
#HiddenNode {
  display: none;
}
</style>
